const GOOGLE_DRIVE_OAUTH_CALLBACK_PATH = "/google-drive/callback";
const GOOGLE_DRIVE_OAUTH_FLOW = "google_drive_oauth";

const GOOGLE_DRIVE_OAUTH_STORAGE_KEYS = {
  code: "google_drive_oauth_code",
  error: "google_drive_oauth_error",
  state: "google_drive_oauth_state",
} as const;

type OAuthCallbackLocation = {
  hash: string;
  pathname: string;
  search: string;
};

type NormalizeGoogleDriveOAuthCallbackParams = {
  location: OAuthCallbackLocation;
  replaceUrl: (url: string) => void;
  storeValue: (key: string, value: string) => void;
};

type DriveOAuthStatePayload = {
  flow?: unknown;
  projectId?: unknown;
  requestedBy?: unknown;
  ts?: unknown;
};

const isGoogleDriveOAuthCallbackPath = (pathname: string) => {
  return pathname === GOOGLE_DRIVE_OAUTH_CALLBACK_PATH;
};

const getFirstNonEmptyValue = (...values: Array<string | null>) => {
  return values.find((value): value is string => Boolean(value)) ?? null;
};

const decodeBase64Url = (value: string) => {
  const normalized = value.replace(/-/g, "+").replace(/_/g, "/");
  const paddingLength = (4 - (normalized.length % 4)) % 4;
  return globalThis.atob(`${normalized}${"=".repeat(paddingLength)}`);
};

const isGoogleDriveOAuthState = (state: string) => {
  try {
    const parsed = JSON.parse(decodeBase64Url(state)) as DriveOAuthStatePayload;

    if (parsed.flow !== undefined && parsed.flow !== GOOGLE_DRIVE_OAUTH_FLOW) {
      return false;
    }

    return (
      typeof parsed.projectId === "string" &&
      parsed.projectId.length > 0 &&
      typeof parsed.requestedBy === "string" &&
      parsed.requestedBy.length > 0 &&
      typeof parsed.ts === "number" &&
      Number.isFinite(parsed.ts)
    );
  } catch {
    return false;
  }
};

/**
 * Runs before Auth0 is initialized. Google Drive and Auth0 both return OAuth
 * parameters named `code` and `state`, so a Drive callback that lands on an
 * old/misconfigured Clovis path must be moved to the dedicated route before
 * Auth0 gets a chance to consume it.
 */
const normalizeGoogleDriveOAuthCallback = (
  params: NormalizeGoogleDriveOAuthCallbackParams
) => {
  const searchParams = new URLSearchParams(params.location.search);
  const hashParams = new URLSearchParams(
    params.location.hash.replace(/^#/, "")
  );
  const state = getFirstNonEmptyValue(
    searchParams.get("state"),
    hashParams.get("state")
  );

  if (!state || !isGoogleDriveOAuthState(state)) {
    return false;
  }

  const code = getFirstNonEmptyValue(
    searchParams.get("code"),
    hashParams.get("code")
  );
  const error = getFirstNonEmptyValue(
    searchParams.get("error"),
    hashParams.get("error")
  );

  if (!code && !error) {
    return false;
  }

  params.storeValue(GOOGLE_DRIVE_OAUTH_STORAGE_KEYS.state, state);
  if (code) {
    params.storeValue(GOOGLE_DRIVE_OAUTH_STORAGE_KEYS.code, code);
  }
  if (error) {
    params.storeValue(GOOGLE_DRIVE_OAUTH_STORAGE_KEYS.error, error);
  }

  if (params.location.pathname !== GOOGLE_DRIVE_OAUTH_CALLBACK_PATH) {
    params.replaceUrl(
      `${GOOGLE_DRIVE_OAUTH_CALLBACK_PATH}${params.location.search}${params.location.hash}`
    );
  }

  return true;
};

export {
  GOOGLE_DRIVE_OAUTH_CALLBACK_PATH,
  GOOGLE_DRIVE_OAUTH_FLOW,
  GOOGLE_DRIVE_OAUTH_STORAGE_KEYS,
  isGoogleDriveOAuthCallbackPath,
  isGoogleDriveOAuthState,
  normalizeGoogleDriveOAuthCallback,
};
